import React, { createContext, useContext, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Spinner, Alert } from "reactstrap";
import { PERMISSIONS, isAdminPanelPermission, permissionForPath } from "../services/roles";
import { isDemoEmail } from "../services/demoMode";


const GuestGuardContext = createContext({ canWrite: true, guardWrite: (fn) => fn });

export function useGuestGuard() {
  return useContext(GuestGuardContext);
}

export default function ProtectedRoute({ children, permission = null }) {
  const { t } = useTranslation();
  const { user, loading, isDemoUser, canWrite: canWriteFn, hasPermission } = useAuth();
  const location = useLocation();
  const demoSession = (typeof isDemoUser === "function" && isDemoUser()) || isDemoEmail(user?.email);
  const [guestAlert, setGuestAlert] = React.useState(false);

  const canWrite = typeof canWriteFn === "function" ? canWriteFn() : !!canWriteFn;

  const guardWrite = useCallback(
    (fn) => {
      return (...args) => {
        if (!canWrite) {
          setGuestAlert(true);
          setTimeout(() => setGuestAlert(false), 3000);
          return;
        }
        return fn(...args);
      };
    },
    [canWrite]
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner color="primary" />
      </div>);

  }

  if (!user) {
    return <Navigate to="/auth/login" replace />;
  }

  const required = permission || permissionForPath(location?.pathname);
  if (demoSession && (isAdminPanelPermission(required) || String(location?.pathname || "").includes("/admin-panel"))) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (required && !hasPermission(required)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <GuestGuardContext.Provider value={{ canWrite, guardWrite }}>
      {demoSession &&
      <Alert color="info" className="mx-3 mt-3 mb-0 py-2 text-center small" data-testid="demo-readonly-banner">
          <i className="ni ni-lock-circle-open mr-2" />
          {t("shell.demoReadOnly")}
        </Alert>
      }
      {guestAlert &&
      <Alert color="warning" className="m-3 text-center" fade>
          <i className="ni ni-lock-circle-open mr-2" />
          {t("shell.readOnly")}
        </Alert>
      }
      {children}
    </GuestGuardContext.Provider>);

}

export { PERMISSIONS };
